import { useState } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle } from "lucide-react"
import client from "../api/client"

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  const { token } = useParams()
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")

    // Validation
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long")
      return
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match")
      return
    }

    setIsLoading(true)

    try {
      const response = await client.post(`/auth/reset-password/${token}`, {
        newPassword,
      })

      setSuccess(true)

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login")
      }, 3000)
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reset password. The link may have expired.")
    } finally {
      setIsLoading(false)
    }
  }

  // Success screen
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="auth-card max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/10 rounded-full mb-6">
            <CheckCircle className="text-green-500" size={48} />
          </div>
          <h1 className="text-3xl font-bold mb-3 text-accent">
            Password Reset Successful!
          </h1>
          <p className="text-text-secondary mb-6">
            Your password has been reset successfully. You can now log in with your new password.
          </p>
          <p className="text-sm text-text-secondary">
            Redirecting to login page in 3 seconds...
          </p>
          <Link
            to="/login"
            className="inline-block mt-6 btn-primary"
          >
            Go to Login
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen flex items-center justify-end p-4 xl:pr-64 lg:pr-14 overflow-hidden">

  <video
    className="absolute inset-0 w-full h-full object-cover"
    src="/images/login-bg.mp4"
    autoPlay
    loop
    muted
    playsInline
  />


  <div className="absolute inset-0 bg-black/40"></div>

  <div className="relative z-10 auth-card max-w-md w-full">
    {/* Header */}
    <div className="text-center mb-8">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-cyan/10 rounded-full mb-4">
        <Lock className="text-accent-cyan" size={32} />
      </div>
      <h1 className="text-xl lg:text-4xl font-bold mb-2">
        <span className="text-accent">Reset Password</span>
      </h1>
      <p className="text-text-secondary">
        Please enter your new password below.
      </p>
    </div>

    {/* Error Message */}
    {error && (
      <div className="mb-6 p-4 bg-red-500/10 border border-red-500 rounded-lg flex items-start gap-3">
        <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
        <div className="text-red-500 text-sm">{error}</div>
      </div>
    )}

    {/* Form */}
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* New Password */}
      <div>
        <label className="form-label">New Password</label>
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={6}
            className="form-input pr-10"
            placeholder="Enter new password"
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <p className="text-xs text-text-secondary mt-1">
          Must be at least 6 characters
        </p>
      </div>

      {/* Confirm Password */}
      <div>
        <label className="form-label">Confirm Password</label>
        <div className="relative">
          <input
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={6}
            className="form-input pr-10"
            placeholder="Confirm new password"
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary text-center justify-center  w-full disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? "Resetting Password..." : "Reset Password"}
      </button>
    </form>

    {/* Back to Login */}
    <div className="mt-6 text-center">
      <Link
        to="/login"
        className="text-accent-cyan hover:text-accent-bright-cyan font-semibold transition-colors"
      >
        Back to Login
      </Link>
    </div>
  </div>
</div>

  )
}
